/**
 * @fileoverview Profile Information tab component for the profile settings page.
 * Provides a comprehensive form for editing user profile data including personal information,
 * professional credentials, and address details based on user type.
 */

import React, { useState, useEffect, useCallback, useContext } from "react";
import {
    Grid,
    Typography,
    Divider,
    Box,
    IconButton,
    CircularProgress,
    Alert,
    Button,
    Card,
    CardContent,
    Tooltip
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import {
    DmsForm,
    DmsTextField,
    DmsSelect,
    DmsTextFieldPhone,
    DmsTextFieldEmail,
    intl,
    LOCALES_FLAG,
    LanguageBaseContext,
    fetchData,
    HTTP_STATUS
} from "dmsbasereact";
import { DmsAutocomplete } from "../../../components/DmsAutoComplete/DmsAutoComplete";
import { getBrazilianStateOptions } from "../../../constants/BrazilianStates";
import { DmsTextFieldCpf } from "../../../components/DmsTextFieldCpf/DmsTextFieldCpf";
import { DmsTextFieldCep } from "../../../components/DmsTextFieldCep";
import { TextFieldCrm } from "../../../components/TextFieldCRM/TextFieldCRM";
import { TextFieldCrf } from "../../../components/TextFieldCRF/TextFieldCRF";

/**
 * Profile Information Tab component
 *
 * Renders a comprehensive profile editing form that adapts based on user type.
 * The form includes different sections:
 * - User Information: Name, email, phone, language, CPF
 * - Doctor Information: CRM number and state (for doctors only)
 * - Pharmacist Information: CRF number and state (for pharmacists only)
 * - Address Information: Complete address fields (if available)
 *
 * @param {Object} props - Component props
 * @param {Object} props.profileData - User profile data object containing person, dms_user, and role-specific data
 * @param {string} props.userType - Type of user ('doctor', 'pharmacist', or other)
 * @param {Function} props.onSubmit - Callback function to handle form submission
 * @returns {JSX.Element} The profile information form tab
 */
export const ProfileInformationTab = ({ profileData, userType, onSubmit }) => {
    const { language: systemLanguage } = useContext(LanguageBaseContext);

    const stateOptions = getBrazilianStateOptions().map(({ value, label }) => ({
        label,
        value,
    }));

    const languageOptions = Object.entries(LOCALES_FLAG).map(([label, value]) => ({
        label,
        value,
    }));

    const hasPerson = profileData.person || false;
    const person = profileData.person;
    const dmsUser = profileData.dms_user || (person ? person.dms_user : null);
    const doctorData = userType === "doctor" ? profileData : null;
    const pharmacistData = userType === "pharmacist" ? profileData : null;

    const [emailChanged, setEmailChanged] = useState(false);

    // Additional CRMs state management
    const [additionalCrms, setAdditionalCrms] = useState([]);
    const [loadingCrms, setLoadingCrms] = useState(false);
    const [showAddCrmForm, setShowAddCrmForm] = useState(false);
    const [newCrm, setNewCrm] = useState({ crm: '', crm_state: '' });
    const [savingCrm, setSavingCrm] = useState(false);
    const [deletingCrmPk, setDeletingCrmPk] = useState(null);
    const [crmError, setCrmError] = useState(null);
    const [isValidatingCrm, setIsValidatingCrm] = useState(false);
    const [isCrmValid, setIsCrmValid] = useState(false);
    const [crmValidationError, setCrmValidationError] = useState("");
    const [crmValidationWarning, setCrmValidationWarning] = useState("");

    const fetchAdditionalCrms = useCallback(async () => {
        if (userType !== "doctor") return;

        setLoadingCrms(true);
        setCrmError(null);
        try {
            const response = await fetchData('/doctor/my_crms', 'GET', true);
            if (response.status === HTTP_STATUS.OK_200) {
                const crms = response.data?.data || response.data || [];
                // Filter out the primary CRM (is_primary = true)
                const additional = crms.filter(c => !c.is_primary);
                setAdditionalCrms(additional);
            }
        } catch (error) {
            setCrmError(intl.get("ErrorLoadingCrms").d("Failed to load additional CRMs"));
        } finally {
            setLoadingCrms(false);
        }
    }, [userType]);

    const resetCrmValidation = useCallback(() => {
        setIsCrmValid(false);
        setCrmValidationError("");
        setCrmValidationWarning("");
    }, []);

    const canValidateCrm = newCrm.crm.trim() !== "" && newCrm.crm_state !== "";

    const handleCrmValidation = useCallback(async () => {
        if (isValidatingCrm || !canValidateCrm) return;

        setIsValidatingCrm(true);
        setCrmValidationError("");
        setCrmValidationWarning("");

        try {
            const response = await fetchData("/doctor/validate_crm", "POST", true, {
                doctor_name: dmsUser?.user_name || "",
                crm: newCrm.crm,
                crm_state: newCrm.crm_state
            });

            const data = response?.data?.data || response?.data || {};

            if (data.is_regular === true) {
                setIsCrmValid(true);
                setCrmValidationError("");

                if (!data.name_matches) {
                    setCrmValidationWarning(
                        intl.get("CrmNameDoesNotMatchWarning").d(
                            "To ensure the standardization of records, we recommend using the same name as registered in the CRM."
                        )
                    );
                }
            } else {
                setIsCrmValid(false);
                setCrmValidationError(
                    intl.get("CrmNotRegular").d("CRM is not regular: ") +
                    (data.cfm_data?.situacao_texto || "")
                );
            }
        } catch (error) {
            setIsCrmValid(false);
            if (error.response?.status === 404) {
                setCrmValidationError(
                    intl.get("CouldNotFindDoctorRegistry").d(
                        "Could not find doctor registry in CFM. Please check the CRM number and state."
                    )
                );
            } else {
                setCrmValidationError(
                    intl.get("CrmValidationError").d("Error validating CRM. Please try again.")
                );
            }
        } finally {
            setIsValidatingCrm(false);
        }
    }, [newCrm, canValidateCrm, isValidatingCrm, dmsUser]);

    const handleAddCrm = useCallback(async () => {
        if (!newCrm.crm || !newCrm.crm_state || !isCrmValid) {
            return;
        }

        setSavingCrm(true);
        setCrmError(null);
        try {
            const response = await fetchData('/doctor/crm', 'POST', true, {
                crm: newCrm.crm,
                crm_state: newCrm.crm_state
            });
            if (response.status === HTTP_STATUS.OK_200 || response.status === HTTP_STATUS.CREATED_201) {
                setNewCrm({ crm: '', crm_state: '' });
                setShowAddCrmForm(false);
                resetCrmValidation();
                await fetchAdditionalCrms();
            } else {
                setCrmError(response.data?.detail || intl.get("ErrorAddingCrm").d("Failed to add CRM"));
            }
        } catch (error) {
            setCrmError(intl.get("ErrorAddingCrm").d("Failed to add CRM"));
        } finally {
            setSavingCrm(false);
        }
    }, [newCrm, isCrmValid, fetchAdditionalCrms, resetCrmValidation]);

    const handleRemoveCrm = useCallback(async (crmPk) => {
        setDeletingCrmPk(crmPk);
        setCrmError(null);
        try {
            const response = await fetchData(`/doctor/crm/${crmPk}`, 'DELETE', true);
            if (response.status === HTTP_STATUS.OK_200 || response.status === HTTP_STATUS.NO_CONTENT_204) {
                await fetchAdditionalCrms();
            } else {
                setCrmError(response.data?.detail || intl.get("ErrorRemovingCrm").d("Failed to remove CRM"));
            }
        } catch (error) {
            setCrmError(intl.get("ErrorRemovingCrm").d("Failed to remove CRM"));
        } finally {
            setDeletingCrmPk(null);
        }
    }, [fetchAdditionalCrms]);

    // Load additional CRMs on mount for doctors
    useEffect(() => {
        if (userType === "doctor") {
            fetchAdditionalCrms();
        }
    }, [userType, fetchAdditionalCrms]);

    const dialogMessage = emailChanged
        ? intl.get("ConfirmSaveEmailChangeMessage").d(
            "Changing your email will update your login. You will be logged out and will need to sign in again with your new email. Do you want to continue?"
          )
        : intl.get("ConfirmSaveMessage").d("Are you sure you want to save the changes?");

    return (
        <DmsForm onSubmit={onSubmit} dialogMessage={dialogMessage}>
            <DmsForm.Body>
                <Grid container spacing={3}>
                    {/* User Information Section */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            {intl.get("UserInformation").d("User Information")}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DmsTextField
                            name="name"
                            label={intl.get("Name").d("Name")}
                            defaultValue={dmsUser.user_name}
                            fullWidth
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DmsTextFieldEmail
                            fullWidth
                            defaultValue={dmsUser.user_email}
                            required
                            onChange={(e) => setEmailChanged(e.target.value.toLowerCase() !== (dmsUser.user_email || "").toLowerCase())}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DmsTextFieldPhone
                            fullWidth
                            defaultValue={dmsUser.user_phone}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <DmsSelect
                            options={languageOptions}
                            label={intl.get("Language").d("Language")}
                            name="user_language"
                            value={dmsUser.user_language || systemLanguage}
                            fullWidth
                            required
                        />
                    </Grid>

                    {/* CPF - only show if user has Person */}
                    {hasPerson && (
                        <Grid item xs={12} md={6}>
                            <DmsTextFieldCpf
                                fullWidth
                                defaultValue={person.cpf}
                                disabled={true}
                            />
                        </Grid>
                    )}

                    {/* Doctor Specific Fields */}
                    {doctorData && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                    {intl.get("DoctorInformation").d("Doctor Information")}
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextFieldCrm
                                    fullWidth
                                    disabled={true}
                                    defaultValue={doctorData.crm}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <DmsAutocomplete
                                    options={stateOptions}
                                    label={intl.get('CRMState').d('CRM State')}
                                    name="crm_state"
                                    value={doctorData.crm_state ? doctorData.crm_state.split(" - ")[0].trim() : ""}
                                    fullWidth
                                    disabled={true}
                                />
                            </Grid>

                            {/* Additional CRMs Section */}
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                    {intl.get("AdditionalCrms").d("Additional CRMs")}
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item xs={12}>
                                {crmError && (
                                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCrmError(null)}>
                                        {crmError}
                                    </Alert>
                                )}

                                {loadingCrms ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={20} />
                                        <Typography variant="body2" color="text.secondary">
                                            {intl.get("Loading").d("Loading...")}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box>
                                        {/* List of Additional CRMs */}
                                        {additionalCrms.length > 0 ? (
                                            <Box sx={{ mb: 2 }}>
                                                {additionalCrms.map((crm) => (
                                                    <Card key={crm.pk} variant="outlined" sx={{ mb: 1 }}>
                                                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                <Box>
                                                                    <Typography variant="body1" fontWeight="medium">
                                                                        CRM: {crm.crm}
                                                                    </Typography>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {intl.get("State").d("State")}: {crm.crm_state}
                                                                    </Typography>
                                                                </Box>
                                                                <Tooltip title={intl.get("Tooltip.Action.Remove").d("Remove")}>
                                                                    <span>
                                                                        <IconButton
                                                                            color="error"
                                                                            onClick={() => handleRemoveCrm(crm.pk)}
                                                                            disabled={deletingCrmPk === crm.pk}
                                                                            size="small"
                                                                        >
                                                                            {deletingCrmPk === crm.pk ? (
                                                                                <CircularProgress size={20} />
                                                                            ) : (
                                                                                <DeleteIcon />
                                                                            )}
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                {intl.get("NoAdditionalCrms").d("No additional CRMs registered.")}
                                            </Typography>
                                        )}

                                        {/* Add CRM Form */}
                                        {showAddCrmForm ? (
                                            <Card variant="outlined" sx={{ p: 2 }}>
                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={12} sm={5}>
                                                        <TextFieldCrm
                                                            fullWidth
                                                            value={newCrm.crm}
                                                            onChange={(e) => {
                                                                setNewCrm({ ...newCrm, crm: e.target.value });
                                                                resetCrmValidation();
                                                            }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <DmsAutocomplete
                                                            options={stateOptions}
                                                            label={intl.get('CRMState').d('CRM State')}
                                                            name="new_crm_state"
                                                            value={newCrm.crm_state}
                                                            onChange={(value) => {
                                                                setNewCrm({ ...newCrm, crm_state: value });
                                                                resetCrmValidation();
                                                            }}
                                                            fullWidth
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={3}>
                                                        <Button
                                                            variant="contained"
                                                            color={isCrmValid ? "success" : "primary"}
                                                            onClick={handleCrmValidation}
                                                            disabled={!canValidateCrm || isValidatingCrm || isCrmValid}
                                                            fullWidth
                                                            sx={{ height: '56px' }}
                                                        >
                                                            {isValidatingCrm ? (
                                                                <CircularProgress size={24} color="inherit" />
                                                            ) : isCrmValid ? (
                                                                <>
                                                                    <CheckIcon sx={{ mr: 1 }} />
                                                                    {intl.get("Validated").d("Validated")}
                                                                </>
                                                            ) : (
                                                                intl.get("ValidateCRM").d("Validate CRM")
                                                            )}
                                                        </Button>
                                                    </Grid>
                                                    {crmValidationError && (
                                                        <Grid item xs={12}>
                                                            <Alert severity="error" sx={{ mt: 1 }}>
                                                                {crmValidationError}
                                                            </Alert>
                                                        </Grid>
                                                    )}
                                                    {crmValidationWarning && (
                                                        <Grid item xs={12}>
                                                            <Alert severity="warning" sx={{ mt: 1 }}>
                                                                {crmValidationWarning}
                                                            </Alert>
                                                        </Grid>
                                                    )}
                                                    {isCrmValid && (
                                                        <Grid item xs={12}>
                                                            <Alert severity="success" sx={{ mt: 1 }}>
                                                                {intl.get("CRMValidatedSuccessfully").d("CRM validated successfully with CFM.")}
                                                            </Alert>
                                                        </Grid>
                                                    )}
                                                    <Grid item xs={12}>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <Button
                                                                variant="contained"
                                                                color="primary"
                                                                onClick={handleAddCrm}
                                                                disabled={savingCrm || !isCrmValid}
                                                                size="small"
                                                            >
                                                                {savingCrm ? (
                                                                    <CircularProgress size={20} color="inherit" />
                                                                ) : (
                                                                    intl.get("Add").d("Add")
                                                                )}
                                                            </Button>
                                                            <Button
                                                                variant="outlined"
                                                                color="secondary"
                                                                onClick={() => {
                                                                    setShowAddCrmForm(false);
                                                                    setNewCrm({ crm: '', crm_state: '' });
                                                                    setCrmError(null);
                                                                    resetCrmValidation();
                                                                }}
                                                                size="small"
                                                            >
                                                                {intl.get("Cancel").d("Cancel")}
                                                            </Button>
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Card>
                                        ) : (
                                            <Button
                                                variant="outlined"
                                                startIcon={<AddIcon />}
                                                onClick={() => setShowAddCrmForm(true)}
                                            >
                                                {intl.get("AddAdditionalCrm").d("Add Additional CRM")}
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </Grid>
                        </>
                    )}

                    {/* Pharmacist Specific Fields */}
                    {pharmacistData && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                    {intl.get("PharmacistInformation").d("Pharmacist Information")}
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextFieldCrf
                                    fullWidth
                                    disabled={true}
                                    defaultValue={pharmacistData.crf}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <DmsAutocomplete
                                    options={stateOptions}
                                    label={intl.get('CRFState').d('CRF State')}
                                    name="crf_state"
                                    value={pharmacistData.crf_state ? pharmacistData.crf_state.split(" - ")[0].trim() : ""}
                                    fullWidth
                                    disabled={true}
                                />
                            </Grid>
                        </>
                    )}

                    {/* Address Information Section - only for users with Person */}
                    {hasPerson && person.address && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                                    {intl.get("AddressInformation").d("Address Information")}
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <DmsTextFieldCep
                                    name="zip_code"
                                    label={intl.get("CEP").d("Zip Code")}
                                    defaultValue={person.address.zip_code}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                        <DmsTextField
                                            name="city"
                                            label={intl.get("City").d("City")}
                                            defaultValue={person.address.city}
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <DmsAutocomplete
                                            options={stateOptions}
                                            label={intl.get('State').d('State')}
                                            name="state"
                                            value={person.address.state ? person.address.state.split(" - ")[0].trim() : ""}
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={8}>
                                        <DmsTextField
                                            name="address"
                                            label={intl.get("Address").d("Address")}
                                            defaultValue={person.address.address}
                                            fullWidth
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <DmsTextField
                                            name="number"
                                            label={intl.get("Number").d("Number")}
                                            defaultValue={person.address.number}
                                            fullWidth
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
            </DmsForm.Body>
        </DmsForm>
    );
};
